export interface MyIpCanvasNodeReference { nodeId: string; ipId: string; }

interface MyIpCanvasSyncOptions<T> {
  initial: MyIpCanvasNodeReference[];
  isCurrent: () => boolean;
  save: (snapshot: T) => Promise<unknown>;
  upsert: (ipId: string) => Promise<void>;
  remove: (ipId: string) => Promise<void>;
}

/** 按快照顺序保存并同步去重 IP 集合；多个快照组共用关联，位置和连线不触发重复绑定。 */
export function createMyIpCanvasSync<T>(options: MyIpCanvasSyncOptions<T>) {
  // 已加载的 IP 作为同步基线；普通保存不重新绑定，只有集合新增时才 apply。
  const synced = new Set(options.initial.map((node) => node.ipId));
  // 网络失败不能证明服务端未写入；后续删除也必须清理这类未确认引用。
  const attempted = new Set<string>();
  let tail: Promise<void> = Promise.resolve();

  /** 切房间/账号/只读后停止后续写入，禁止旧画布任务携带新空间登录头。 */
  function assertCurrent() {
    if (!options.isCurrent()) throw new Error('画布上下文已变化，请返回当前画布后重试');
  }

  /** 捕获调用时的去重 IP 集合，最后一个同 IP 节点删除后才解除画布关联。 */
  function save(snapshot: T, references: MyIpCanvasNodeReference[]): Promise<void> {
    const next = new Set(references.map((node) => node.ipId));
    const pending = tail.then(async () => {
      assertCurrent();
      await options.save(snapshot);
      assertCurrent();
      for (const ipId of new Set([...synced, ...attempted])) {
        if (!next.has(ipId)) {
          // 引用必须按快照顺序确认，失败时保留已成功进度。
          // eslint-disable-next-line no-await-in-loop
          await options.remove(ipId);
          synced.delete(ipId);
          attempted.delete(ipId);
          assertCurrent();
        }
      }
      for (const ipId of next) {
        if (!synced.has(ipId)) {
          attempted.add(ipId);
          // 禁止旧 upsert 晚于后续删除完成而复活节点。
          // eslint-disable-next-line no-await-in-loop
          await options.upsert(ipId);
          synced.add(ipId);
          attempted.delete(ipId);
          assertCurrent();
        }
      }
    });
    // 队列继续运作，但每个调用方仍收到自己那次失败，不吞掉应用错误。
    tail = pending.catch(() => undefined);
    return pending;
  }

  return { save };
}
